// Game rendering — uses engine's SceneRenderer + adds entity instances

import { FP } from "../engine/fixed.js";
import { mat4Perspective, mat4LookAt, mat4Multiply } from "../engine/math3d.js";
import type { GPU } from "../engine/gpu.js";
import type { CameraState, DebugInfo, RenderContext } from "../engine/engine.js";
import { SceneRenderer, hexToRgb } from "../engine/render.js";
import { DebugPanel } from "../engine/debugpanel.js";
import { buildSceneRender, type SceneConfig } from "../engine/scene.js";
import { SimEventType } from "../engine/events.js";
import { processAudioEvents, updateListener } from "./audio.js";
import { formatTicks, type ScoreboardConfig } from "./scoring.js";
import { FONT } from "./ui/theme.js";
import { surfScoreboard } from "./modes/surfScore.js";
import { Scoreboard } from "./ui/scoreboard.js";

const INTS_PER_ENTITY = 7; // x, y, z, size, vx, vy, vz

let renderer: SceneRenderer;
let panel: DebugPanel;
let canvasW = 800;
let canvasH = 600;
let initialized = false;

function init(gpu: GPU, scene: SceneConfig): void {
  canvasW = gpu.canvas.width;
  canvasH = gpu.canvas.height;

  renderer = new SceneRenderer(gpu, buildSceneRender(scene));

  // Debug panel
  const c = renderer.cfg;
  panel = new DebugPanel();
  panel.addSeparator("Post Processing");
  panel.add("exposure", 0, 3, c.exposure, 0.01, v => { c.exposure = v; renderer.post.config.exposure = v; });
  panel.add("bloomStrength", 0, 0.5, c.bloomStrength, 0.005, v => { c.bloomStrength = v; renderer.post.config.bloomStrength = v; });
  panel.add("bloomThreshold", 0, 2, c.bloomThreshold, 0.05, v => { c.bloomThreshold = v; renderer.post.config.bloomThreshold = v; });
  panel.addSeparator("Directional Light");
  const sunLen0 = Math.sqrt(c.sunDir[0]**2 + c.sunDir[1]**2 + c.sunDir[2]**2);
  let sunYaw = Math.atan2(c.sunDir[0], c.sunDir[2]);
  let sunPitch = sunLen0 > 0.001 ? Math.asin(Math.min(1, c.sunDir[1] / sunLen0)) : 0.5;
  let sunIntensity = sunLen0;
  const updateSunDir = () => {
    c.sunDir[0] = Math.cos(sunPitch) * Math.sin(sunYaw) * sunIntensity;
    c.sunDir[1] = Math.sin(sunPitch) * sunIntensity;
    c.sunDir[2] = Math.cos(sunPitch) * Math.cos(sunYaw) * sunIntensity;
  };
  panel.add("sunYaw", -3.14, 3.14, sunYaw, 0.02, v => { sunYaw = v; updateSunDir(); });
  panel.add("sunPitch", 0.05, 1.55, sunPitch, 0.02, v => { sunPitch = v; updateSunDir(); });
  panel.add("sunIntensity", 0, 1, sunIntensity, 0.01, v => { sunIntensity = v; updateSunDir(); });
  panel.addSeparator("GI & Reflections");
  panel.add("giStrength", 0, 2, c.giStrength, 0.01, v => { c.giStrength = v; });
  panel.add("reflStrength", 0, 2, c.reflStrength, 0.01, v => { c.reflStrength = v; });
  panel.add("reflTint", 0.5, 3, c.reflTint, 0.05, v => { c.reflTint = v; });
  panel.add("aoStrength", 0, 3, c.aoStrength, 0.01, v => { c.aoStrength = v; });
  panel.addSeparator("Volumetrics");
  panel.add("volDensity", 0, 0.1, c.volDensity, 0.001, v => { c.volDensity = v; renderer.vol.config.density = v; });
  panel.add("volStrength", 0, 3, c.volStrength, 0.05, v => { c.volStrength = v; renderer.vol.config.strength = v; });
  panel.addSeparator("Fog / Sky");
  panel.add("fogBrightness", 0, 0.2, c.fogColor[0], 0.001, v => { c.fogColor = [v, v * 1.1, v * 1.4]; });
  panel.add("fogDensity", 0, 0.05, c.fogDensity, 0.001, v => { c.fogDensity = v; });
  panel.addSeparator("Ambient");
  panel.add("ambient", 0, 0.1, c.ambient, 0.001, v => { c.ambient = v; });

  const ot = renderer.oceanTweaks;
  const oc = renderer.ocean;
  panel.addSeparator("Ocean Surface");
  panel.addColor("deepColor", "#011f29", (r, g, b) => { oc.deepColor = [r, g, b]; });
  panel.addColor("midColor", "#031f1a", (r, g, b) => { oc.midColor = [r, g, b]; });
  panel.addColor("crestColor", "#05291a", (r, g, b) => { oc.crestColor = [r, g, b]; });
  panel.addSeparator("Water Composite");
  panel.add("refractionStr", 0, 0.2, ot.refractionStr, 0.005, v => { ot.refractionStr = v; });
  panel.add("absorbMul", 0, 5, ot.absorbMul, 0.1, v => { ot.absorbMul = v; });
  panel.add("absorbR", 0, 2, ot.absorbCoeff[0], 0.01, v => { ot.absorbCoeff[0] = v; });
  panel.add("absorbG", 0, 0.5, ot.absorbCoeff[1], 0.005, v => { ot.absorbCoeff[1] = v; });
  panel.add("absorbB", 0, 0.5, ot.absorbCoeff[2], 0.005, v => { ot.absorbCoeff[2] = v; });
  panel.add("ndlMul", 0, 5, ot.ndlMul, 0.1, v => { ot.ndlMul = v; });
  panel.add("specPower", 100, 4000, ot.specPower, 50, v => { ot.specPower = v; });
  panel.add("sparkleMul", 0, 20, ot.sparkleMul, 0.5, v => { ot.sparkleMul = v; });
  panel.add("sssMul", 0, 5, ot.sssMul, 0.1, v => { ot.sssMul = v; });

  initialized = true;
}

// Timer HUD
let timerEl: HTMLDivElement | null = null;
let timerTicks = 0;
let timerStopped = false;
let finalTime = "";

function ensureTimerHud() {
  if (timerEl) return;
  timerEl = document.createElement("div");
  timerEl.style.cssText =
    `position:fixed;top:50px;left:50%;transform:translateX(-50%);color:#fff;font:bold 24px ${FONT};` +
    "text-shadow:0 0 8px rgba(0,0,0,0.8);pointer-events:none;z-index:500;";
  document.body.appendChild(timerEl);
}

// Velocity HUD
let velocityEl: HTMLDivElement | null = null;

function ensureVelocityHud() {
  if (velocityEl) return;
  velocityEl = document.createElement("div");
  velocityEl.style.cssText =
    `position:fixed;bottom:60px;left:50%;transform:translateX(-50%);color:#fff;font:bold 20px ${FONT};` +
    "text-shadow:0 0 8px rgba(0,0,0,0.8);pointer-events:none;z-index:500;opacity:0.8;";
  document.body.appendChild(velocityEl);
}

// Use formatTicks from scoring.ts (shared with scoreboard)
const formatTime = formatTicks;

// Vote prompt — shown after completing a run on a community map
let voteEl: HTMLDivElement | null = null;
let voteShown = false; // only show once per session
let voteDismissTimer = 0;

function showVotePrompt(mapCode: string, relayHttp: string): void {
  if (voteShown) return;
  const voteKey = `ironbolt_vote_${mapCode}`;
  if (localStorage.getItem(voteKey) !== null) return; // already voted
  voteShown = true;

  let selectedUp: boolean | undefined;
  let selectedDiff: number | undefined;

  voteEl = document.createElement("div");
  voteEl.style.cssText =
    "position:fixed;top:90px;left:50%;transform:translateX(-50%);" +
    "display:flex;gap:16px;align-items:center;z-index:500;pointer-events:auto;";

  const btnStyle =
    `padding:4px 10px;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.2);` +
    `color:#fff;cursor:pointer;font:12px ${FONT};`;
  const activeStyle =
    `padding:4px 10px;background:rgba(92,140,180,0.4);border:1px solid rgba(92,140,180,0.6);` +
    `color:#fff;cursor:pointer;font:12px ${FONT};`;

  // Quality vote
  const qualLabel = document.createElement("span");
  qualLabel.textContent = "Rate";
  qualLabel.style.cssText = `color:rgba(255,255,255,0.5);font:11px ${FONT};`;
  voteEl.appendChild(qualLabel);

  const upBtn = document.createElement("button");
  upBtn.textContent = "\u25B2";
  upBtn.style.cssText = btnStyle;
  const downBtn = document.createElement("button");
  downBtn.textContent = "\u25BC";
  downBtn.style.cssText = btnStyle;

  upBtn.addEventListener("click", () => { selectedUp = true; upBtn.style.cssText = activeStyle; downBtn.style.cssText = btnStyle; });
  downBtn.addEventListener("click", () => { selectedUp = false; downBtn.style.cssText = activeStyle; upBtn.style.cssText = btnStyle; });
  voteEl.append(upBtn, downBtn);

  // Separator
  const sep = document.createElement("span");
  sep.style.cssText = "width:1px;height:16px;background:rgba(255,255,255,0.15);";
  voteEl.appendChild(sep);

  // Difficulty vote
  const diffLabel = document.createElement("span");
  diffLabel.textContent = "Difficulty";
  diffLabel.style.cssText = `color:rgba(255,255,255,0.5);font:11px ${FONT};`;
  voteEl.appendChild(diffLabel);

  const diffBtns: HTMLButtonElement[] = [];
  const DIFF_LABELS = ["Easy", "Medium", "Hard"];
  for (let i = 0; i < 3; i++) {
    const btn = document.createElement("button");
    btn.textContent = DIFF_LABELS[i];
    btn.style.cssText = btnStyle;
    btn.addEventListener("click", () => {
      selectedDiff = i;
      for (let j = 0; j < 3; j++) diffBtns[j].style.cssText = j === i ? activeStyle : btnStyle;
    });
    diffBtns.push(btn);
    voteEl.appendChild(btn);
  }

  // Submit
  const submitBtn = document.createElement("button");
  submitBtn.textContent = "Submit";
  submitBtn.style.cssText =
    `padding:4px 14px;background:rgba(92,140,180,0.5);border:1px solid rgba(92,140,180,0.6);` +
    `color:#fff;cursor:pointer;font:bold 11px ${FONT};letter-spacing:1px;`;
  submitBtn.addEventListener("click", async () => {
    if (selectedUp === undefined && selectedDiff === undefined) { dismissVote(); return; }
    try {
      const { voteMap } = await import("../engine/launcher.js");
      await voteMap(relayHttp, mapCode, selectedUp, selectedDiff);
      localStorage.setItem(voteKey, "done");
    } catch { /* ignore */ }
    dismissVote();
  });
  voteEl.appendChild(submitBtn);

  document.body.appendChild(voteEl);
  voteDismissTimer = window.setTimeout(dismissVote, 12000);
}

function dismissVote(): void {
  if (voteEl) { voteEl.remove(); voteEl = null; }
  clearTimeout(voteDismissTimer);
}

export function createRenderer(gpu: GPU, sceneJson: string, context?: RenderContext) {
  const scene: SceneConfig = JSON.parse(sceneJson);

  // Resolve mode scoreboard config
  const mode = (context?.gameConfig?.mode as string) ?? scene.mode ?? "surf";
  const scoreConfig: ScoreboardConfig = surfScoreboard(); // all modes use surf scoring for now
  let scoreboard: Scoreboard | null = null;

  // Cache hex→RGB conversions — colors are fixed per entity, no need to reparse every frame
  const rgbCache = new Map<string, [number, number, number]>();
  function cachedRgb(hex: string): [number, number, number] {
    let rgb = rgbCache.get(hex);
    if (!rgb) { rgb = hexToRgb(hex); rgbCache.set(hex, rgb); }
    return rgb;
  }

  // Reusable lerp output — avoids per-call array allocation in the render loop
  const _lerpOut: [number, number, number] = [0, 0, 0];

  /** Lerp a position between two buffers. Writes to shared _lerpOut. */
  function lerpEntity(prev: Int32Array | null, curr: Int32Array, off: number, alpha: number): [number, number, number] {
    const cx = FP.toFloat(curr[off]);
    const cy = FP.toFloat(curr[off + 1]);
    const cz = FP.toFloat(curr[off + 2]);
    if (!prev || alpha >= 1) {
      _lerpOut[0] = cx; _lerpOut[1] = cy; _lerpOut[2] = cz;
    } else {
      const px = FP.toFloat(prev[off]);
      const py = FP.toFloat(prev[off + 1]);
      const pz = FP.toFloat(prev[off + 2]);
      _lerpOut[0] = px + (cx - px) * alpha;
      _lerpOut[1] = py + (cy - py) * alpha;
      _lerpOut[2] = pz + (cz - pz) * alpha;
    }
    return _lerpOut;
  }

  return function render(buffer: Int32Array, prevBuffer: Int32Array | null, alpha: number, meta: unknown, camera: CameraState, debugInfo: DebugInfo): void {
    if (!initialized) init(gpu, scene);

    // Ensure prevBuffer has the same entity layout before interpolating
    const entityColors = meta as string[];
    const count = buffer[1];
    const canLerp = prevBuffer && prevBuffer[1] === count;

    // Camera from player entity (interpolated)
    let camX = 0, camY = 2, camZ = 0;
    let playerVx = 0, playerVz = 0;
    for (let i = 0; i < count; i++) {
      if (i === camera.localPlayerId) {
        const off = 2 + i * INTS_PER_ENTITY;
        const [ex, ey, ez] = lerpEntity(canLerp ? prevBuffer : null, buffer, off, alpha);
        camX = ex;
        camY = ey + 1.7;
        camZ = ez;
        debugInfo.playerX = ex;
        debugInfo.playerY = ey;
        debugInfo.playerZ = ez;
        playerVx = FP.toFloat(buffer[off + 4]);
        playerVz = FP.toFloat(buffer[off + 6]);
        break;
      }
    }
    const lookX = camX + Math.sin(-camera.yaw) * Math.cos(camera.pitch);
    const lookY = camY + Math.sin(camera.pitch);
    const lookZ = camZ + Math.cos(-camera.yaw) * Math.cos(camera.pitch);

    const proj = mat4Perspective(Math.PI / 3, canvasW / canvasH, 0.1, 2000);
    const view = mat4LookAt([camX, camY, camZ], [lookX, lookY, lookZ], [0, 1, 0]);
    const viewProj = mat4Multiply(proj, view);

    renderer.beginInstances();

    // Other entities (interpolated positions)
    for (let i = 0; i < count; i++) {
      if (i === camera.localPlayerId) continue;
      const off = 2 + i * INTS_PER_ENTITY;
      const size = buffer[off + 3];
      const [ex, ey, ez] = lerpEntity(canLerp ? prevBuffer : null, buffer, off, alpha);
      renderer.addInstance(ex, ey + size / 2, ez, size, size, size, entityColors[i], 0, 0.7);
    }

    // GI boxes (current positions only — GI is low-frequency)
    for (let i = 0; i < count; i++) {
      const off = 2 + i * INTS_PER_ENTITY;
      const [r, g, b] = cachedRgb(entityColors[i]);
      const size = buffer[off + 3];
      renderer.gi.addBox(FP.toFloat(buffer[off]), FP.toFloat(buffer[off+1]) + size/2, FP.toFloat(buffer[off+2]), size, size, size, r, g, b);
    }

    renderer.renderFrame(viewProj, [camX, camY, camZ]);
    debugInfo.triCount = renderer.triCount;

    // Read sim events from buffer tail
    const evtOff = 2 + count * INTS_PER_ENTITY;
    if (evtOff < buffer.length) {
      const eventCount = buffer[evtOff];
      for (let i = 0; i < eventCount; i++) {
        const base = evtOff + 1 + i * 5;
        const type = buffer[base];
        const pid = buffer[base + 1];
        const ticks = buffer[base + 2];

        // Feed ALL player events to scoring
        scoreConfig.processEvent(pid, type, ticks, buffer[base + 3]);

        // Personal timer HUD — local player only
        if (pid === camera.localPlayerId) {
          if (type === SimEventType.TIMER_UPDATE) {
            timerTicks = ticks; timerStopped = false;
          } else if (type === SimEventType.TIMER_STOP) {
            timerTicks = ticks; timerStopped = true;
            finalTime = formatTime(ticks);
            // Show vote prompt for community maps
            if (context?.mapCode && context?.relayHttp) {
              showVotePrompt(context.mapCode, context.relayHttp);
            }
          }
        }
      }
    }

    // Update timer HUD
    ensureTimerHud();
    if (timerStopped) {
      timerEl!.textContent = finalTime;
      timerEl!.style.color = "#0f0";
    } else if (timerTicks > 0) {
      timerEl!.textContent = formatTime(timerTicks);
      timerEl!.style.color = "#fff";
    } else {
      timerEl!.textContent = "";
    }

    // Update velocity HUD — horizontal speed
    ensureVelocityHud();
    const hSpeed = Math.sqrt(playerVx * playerVx + playerVz * playerVz);
    velocityEl!.textContent = `${Math.round(hSpeed)} u/s`;

    // Scoreboard (lazy init — needs localPlayerId from first render)
    if (!scoreboard) {
      scoreboard = new Scoreboard(scoreConfig, {
        serverName: context?.serverName ?? "Local",
        mapName: context?.mapName ?? "unknown",
        mapCreator: context?.mapCreator,
        modeName: mode,
        presetName: (context?.gameConfig?.preset as string) ?? "classic",
      }, camera.localPlayerId);
    }
    scoreboard.update(count, buffer);

    // Audio: update listener + process sim events
    updateListener(camX, camY, camZ, camera.yaw, camera.pitch);
    processAudioEvents(buffer, count, camera.localPlayerId);
  };
}
